import classNames from 'classnames';
import {type ReactNode, useCallback, useEffect, useRef, useState} from 'react';

import {Typography, VARIANTS} from '@/components/base/typography/Typography';
import {OpenButton} from '@/components/common/faq/OpenButton';
import {FEATURES} from '@/feature-flags';
import {useInjection} from '@/hooks/use-injection';
import {useMemoTypography} from '@/hooks/use-memo-typography';
import {XrayEventId} from '@/services/metrics/constants';
import {IMetricsService} from '@/services/metrics/IMetricsService';

import styles from './Question.module.css';

interface QuestionProps {
	question: string;
	answer: string | ReactNode;
	withSeparator: boolean;
	className?: string;
	questionClassName?: string;
	answerHeightClassName?: string;
	answerClassName?: string;
	withMicroData?: boolean;
}

// eslint-disable-next-line max-lines-per-function
export function Question({
	question,
	answer,
	withSeparator,
	className,
	questionClassName,
	answerHeightClassName,
	answerClassName,
	withMicroData,
}: QuestionProps) {
	const [metricsService] = useInjection(IMetricsService);
	const tpQuestion = useMemoTypography(question);
	const tpAnswer = useMemoTypography(answer);
	const answerRef = useRef(null);
	const innerAnswerRef = useRef(null);
	const [customStyles, setCustomStyles] = useState({});

	const [isOpen, setOpen] = useState(false);

	const onClick = useCallback(() => {
		setOpen(!isOpen);
		metricsService.sendEvent({
			xrayEvents: [
				{
					name: XrayEventId.clickFaq,
					params: {
						text: question,
						action: isOpen ? 'off' : 'on',
						is_sng: FEATURES.IS_SNG_USER,
					},
				},
			],
		});
	}, [isOpen, metricsService, question]);

	useEffect(() => {
		if (isOpen && answerRef.current && innerAnswerRef.current) {
			const answerHeight = answerRef.current.getBoundingClientRect?.().height;
			const innerAnswerHeight = innerAnswerRef.current.getBoundingClientRect?.().height;
			if (innerAnswerHeight > answerHeight) {
				setCustomStyles((s) => {
					return {
						...s,
						maxHeight: innerAnswerHeight,
					};
				});
			}
		} else {
			setCustomStyles((s) => {
				return {
					...s,
					maxHeight: '',
				};
			});
		}
	}, [isOpen]);

	return (
		<div
			className={classNames(styles.root, className, {[styles.separator]: withSeparator})}
			{...(withMicroData
				? {
						itemProp: 'mainEntity',
						itemType: 'https://schema.org/Question',
				  }
				: {})}
		>
			<div
				className={classNames(styles.question, questionClassName)}
				onClick={onClick}
				{...(withMicroData ? {itemProp: 'name'} : {})}
			>
				{tpQuestion}
			</div>
			<div className={styles.control}>
				<OpenButton isOpen={isOpen} onClick={onClick} />
			</div>
			<div
				ref={answerRef}
				style={customStyles}
				className={classNames(styles.answerWrapper, {
					[classNames(styles.displayVisible, answerHeightClassName)]: isOpen,
				})}
				{...(withMicroData
					? {
							itemScope: true,
							itemProp: 'acceptedAnswer',
							itemType: 'https://schema.org/Answer',
					  }
					: {})}
			>
				<Typography
					ref={innerAnswerRef}
					as="div"
					variant={VARIANTS.accentText}
					className={classNames(styles.answer, answerClassName)}
					{...(withMicroData
						? {
								itemProp: 'text',
						  }
						: {})}
				>
					{typeof answer === 'string' ? tpAnswer : answer}
				</Typography>
			</div>
		</div>
	);
}
